import React, { useEffect, useState } from "react";
import i18next from "i18next";
import {
  Button,
  Input,
  Text,
  Alert,
  Field,
  NativeSelect,
  RadioGroup,
  HStack,
  Stack,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../../../App";
import { t } from "i18next";
import { Project, UpdateProject } from "../../models/Project";
import { CreateOrUpdateProjectSchema } from "./CreateOrUpdateProjectSchema";
import { LocationApi } from "../../../shared/services/LocationApi";
import { useStores } from "../../../shared/stores/RootStore";
import { CustomDialog } from "../../../shared/views/CustomDialog";
import { DistanceUnit } from "@keita/lanepatrol-sdk";

const initialValues: UpdateProject = {
  country: "",
  city: "",
  name: "",
  latitude: 0,
  longitude: 0,
  distanceUnit: DistanceUnit.Metric,
  rightHandTraffic: true,
};

const CreateOrUpdateProjectBase: React.FC<{
  onClose: () => void;
  project?: Project;
}> = ({ project, onClose }) => {
  const { projectStore, accountStore } = useStores();
  const navigate = useNavigate();
  const account = accountStore.selectedUserAccount!;

  const [countries, setCountries] = useState<LocationApi.Country[]>();
  const [cities, setCities] = useState<LocationApi.City[]>();
  const [selectedCountry, setSelectedCountry] = useState<LocationApi.Country>();

  useEffect(() => {
    LocationApi.listCountries().then(setCountries);
    // eslint-disable-next-line
  }, []);

  const selectCountry = (iso2: string) => {
    if (countries) {
      const countryDetail = countries.find((c) => c.iso2 === iso2);

      setSelectedCountry(countryDetail);
    }
  };

  // pre-select country
  useEffect(() => {
    if (project) {
      selectCountry(project.country);
    }
    // eslint-disable-next-line
  }, [countries, project]);

  useEffect(() => {
    if (selectedCountry) {
      setCities(undefined);
      LocationApi.listCities(selectedCountry.iso2).then(setCities);
    }
    // eslint-disable-next-line
  }, [selectedCountry]);

  const formik = useFormik({
    initialValues: project
      ? {
          name: project.name,
          country: project.country,
          city: project.city,
          latitude: project.latitude,
          longitude: project.longitude,
          distanceUnit: project.distanceUnit,
          rightHandTraffic: project.rightHandTraffic,
        }
      : initialValues,

    onSubmit: (values: UpdateProject) => {
      const city = cities?.find((c) => c.name === values.city);

      if (!project) {
        projectStore
          .addProject({
            accountId: account.accountId,
            name: values.name,
            country: values.country,
            city: values.city,
            latitude: city?.latitude ?? values.latitude,
            longitude: city?.longitude ?? values.longitude,
            distanceUnit: values.distanceUnit,
            rightHandTraffic: values.rightHandTraffic,
          })
          .then((project) => {
            navigate(appRoutes.project(project.id));
          });
      } else {
        projectStore.updateProject(
          {
            name: values.name,
            country: values.country,
            city: values.city,
            latitude: city?.latitude ?? values.latitude,
            longitude: city?.longitude ?? values.longitude,
            distanceUnit: values.distanceUnit,
            rightHandTraffic: values.rightHandTraffic,
          },
          project.id!,
        );
      }
      formik.setValues(formik.initialValues);
      onClose();
    },

    validationSchema: CreateOrUpdateProjectSchema,
  });

  useEffect(() => {
    formik.setValues(formik.initialValues);
    // eslint-disable-next-line
  }, []);

  return (
    <CustomDialog
      header={
        !project
          ? `${i18next.t("shared:project.modal.titleNew")}`
          : `${i18next.t("shared:project.modal.title")}`
      }
      footer={
        <Button
          colorPalette="primary"
          type="submit"
          form="createOrUpdateProject"
          disabled={!formik.isValid || !formik.dirty}
        >
          {`${i18next.t("shared:project.submit.done")}`}
        </Button>
      }
      onClose={onClose}
    >
      <form id="createOrUpdateProject" onSubmit={formik.handleSubmit}>
        <Stack gap={4}>
          <Field.Root disabled={!countries} required>
            <Field.Label>{`${i18next.t(
              "shared:project.modal.country",
            )}`}</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={formik.values.country}
                name="country"
                onChange={(e) => {
                  selectCountry(e.target.value);
                  formik.handleChange(e);
                }}
                placeholder={`${i18next.t(
                  "shared:project.modal.placeholder.country",
                )}`}
              >
                {countries?.map((c, i) => (
                  <option value={c.iso2} key={i}>
                    {c.name.en}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root disabled={!cities} required>
            <Field.Label>{`${i18next.t(
              "shared:project.modal.city",
            )}`}</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                name="city"
                onChange={formik.handleChange}
                placeholder={`${i18next.t(
                  "shared:project.modal.placeholder.city",
                )}`}
                value={formik.values.city}
              >
                {cities?.map((city, i) => (
                  <option value={city.name} key={i}>
                    {city.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>

          <Field.Root invalid={!!formik.errors.name} required>
            <Field.Label>
              {`${i18next.t("shared:project.modal.project.title")}`}
            </Field.Label>
            <Input
              required={true}
              placeholder={`${i18next.t(
                "shared:project.modal.project.placeholder",
              )}`}
              id="name"
              name="name"
              onChange={formik.handleChange}
              value={formik.values.name}
            />
            <Field.ErrorText>{formik.errors.name}</Field.ErrorText>
          </Field.Root>

          <Field.Root>
            <Field.Label>Distance unit</Field.Label>
            <RadioGroup.Root
              name="distanceUnit"
              colorPalette={"primary"}
              value={formik.values.distanceUnit}
              onValueChange={(details) => {
                console.log(details);
                formik.setFieldValue("distanceUnit", details.value);
              }}
            >
              <HStack gap="6">
                <RadioGroup.Item value={DistanceUnit.Metric}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>Metric</RadioGroup.ItemText>
                </RadioGroup.Item>

                <RadioGroup.Item value={DistanceUnit.Imperial}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>Imperial</RadioGroup.ItemText>
                </RadioGroup.Item>
              </HStack>
            </RadioGroup.Root>
          </Field.Root>

          <Field.Root>
            <Field.Label>Drive on</Field.Label>
            <RadioGroup.Root
              colorPalette={"primary"}
              value={formik.values.rightHandTraffic ? "right" : "left"}
              onValueChange={({ value }) => {
                formik.setFieldValue("rightHandTraffic", value === "right");
              }}
            >
              <HStack gap="6">
                <RadioGroup.Item value="left">
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>Left side</RadioGroup.ItemText>
                </RadioGroup.Item>

                <RadioGroup.Item value="right">
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>Right side</RadioGroup.ItemText>
                </RadioGroup.Item>
              </HStack>
            </RadioGroup.Root>
          </Field.Root>
        </Stack>
      </form>

      {!project && (
        <Alert.Root status="info" mt={6}>
          <Alert.Indicator />
          <Alert.Content>
            <div>
              <Text fontSize="sm" fontWeight="semibold">
                {t("shared:project.modal.project.accountAlert")}
              </Text>
              <Text>{account.name}</Text>
            </div>
          </Alert.Content>
        </Alert.Root>
      )}
    </CustomDialog>
  );
};

export const CreateOrUpdateProjectModal = observer(CreateOrUpdateProjectBase);
